import React, { useState, useEffect } from "react";
import { useAuth } from "@/features/auth/context/AuthContext";
import {
  getUserPreferences,
  updateUserCookiePreferences,
  CookiePreferences
} from "@/lib/supabase";
import { CookieBanner } from "./CookieBanner";
import { useNavigate } from "react-router-dom";
import { isAdmin, isPartner } from "@/lib/auth-utils";

const CURRENT_VERSION = "1.0";

export function CookieScriptLoader({
  children
}: {
  children: React.ReactNode;
}) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState<CookiePreferences | null>(
    null
  );
  const [showBanner, setShowBanner] = useState(false);

  // Load preferences
  useEffect(() => {
    async function loadPreferences() {
      if (!profile || !profile.id) {
        // If no user, we might want to show banner but we can't save to DB.
        // Requirement says "User consent must be persisted server-side".
        // If neutral state (logged out), maybe we don't show banner or we show but save to localStorage?
        // But requirement emphasizes Supabase.
        // Assuming this is only for logged-in users.
        return;
      }

      try {
        const data = await getUserPreferences(profile.id, profile.role);
        if (data?.preferences?.cookies) {
          const userCookies = data.preferences.cookies as CookiePreferences;
          setPreferences(userCookies);
          // If consent is missing or version mismatch, show banner
          if (
            userCookies.consent_status === "managed" ||
            userCookies.consent_status === "accepted" ||
            userCookies.consent_status === "rejected"
          ) {
            // Valid status
            setShowBanner(false);
          } else {
            setShowBanner(true);
          }
        } else {
          // No cookies preferences found
          setShowBanner(true);
        }
      } catch (error) {
        console.error("Error checking cookie preferences:", error);
      }
    }

    loadPreferences();
  }, [profile]);

  const handleAcceptAll = async () => {
    if (!profile || !profile.id) return;
    const newPrefs: CookiePreferences = {
      consent_status: "accepted",
      version: CURRENT_VERSION,
      timestamp: new Date().toISOString(),
      categories: {
        strictly_necessary: true,
        analytics: true,
        marketing: true
      }
    };
    await savePreferences(newPrefs);
  };

  const handleRejectAll = async () => {
    if (!profile || !profile.id) return;
    const newPrefs: CookiePreferences = {
      consent_status: "rejected",
      version: CURRENT_VERSION,
      timestamp: new Date().toISOString(),
      categories: {
        strictly_necessary: true,
        analytics: false,
        marketing: false
      }
    };
    await savePreferences(newPrefs);
  };

  const savePreferences = async (newPrefs: CookiePreferences) => {
    try {
      await updateUserCookiePreferences(profile.id, profile.role, newPrefs);
      setPreferences(newPrefs);
      setShowBanner(false);
    } catch (error) {
      console.error("Failed to save cookie preferences", error);
    }
  };

  const handleManage = () => {
    // Navigate based on role
    if (
      isAdmin(profile.role) ||
      isPartner(profile.role) ||
      profile.isSuperUser
    ) {
      navigate("/settings/manage-cookies");
    } else {
      navigate("/account/manage-cookies");
    }
  };

  return (
    <>
      {children}

      {showBanner && (
        <CookieBanner
          onAcceptAll={handleAcceptAll}
          onRejectAll={handleRejectAll}
          onManage={handleManage}
        />
      )}

      {/* Conditionally render scripts based on preferences */}
      {preferences?.categories.analytics && (
        <>{/* TODO: Paste Google Analytics script URL/tag here */}</>
      )}

      {preferences?.categories.marketing && (
        <>{/* TODO: Paste Marketing script URL/tag here */}</>
      )}
    </>
  );
}
